import React, { useState, useEffect } from 'react';
import {
  Table,
  Header,
  Button,
  SpaceBetween,
  StatusIndicator,
  Box,
  Pagination,
  TextFilter,
  Spinner,
  Alert,
} from '@cloudscape-design/components';
import { useCollection } from '@cloudscape-design/collection-hooks';
import { getGLExports } from 'src/api/hooks/getGLExports';
import { useFlashbar } from 'src/common/hooks/useFlashbar';
import { DownloadModal } from './DownloadModal';
import { ExportRecord } from '@amzn/controllershiphub-typescript-client';

interface ExportsTableProps {
  controlId: string;
}

const statusMap: Record<string, any> = {
  PENDING: 'pending',
  CANCELED: 'error',
  COMPLETED: 'success',
  EXPIRED: 'error',
};

const getFileExtension = (fileLink: string) => {
  if (!fileLink || typeof fileLink !== 'string') {
    return 'N/A';
  }
  const parts = fileLink.split('.');
  return parts.length > 1 ? parts.pop()?.toUpperCase() : 'N/A';
};

const columnDefinitions = [
  {
    id: 'name',
    header: 'Name',
    cell: (item: ExportRecord) => item.exportInfo?.exportName || 'N/A',
    sortingField: 'exportInfo.exportName',
  },
  {
    id: 'status',
    header: 'Status',
    cell: (item: ExportRecord) => (
      <StatusIndicator type={statusMap[item.status || 'PENDING']}>
        {item.status || 'PENDING'}
      </StatusIndicator>
    ),
    sortingField: 'status',
  },
  {
    id: 'fileType',
    header: 'File Type',
    cell: (item: ExportRecord) => getFileExtension(item.fileLinks?.[0] || '') || 'CSV',
    sortingField: 'fileType',
  },
  {
    id: 'dataset',
    header: 'Dataset Type',
    cell: (item: ExportRecord) =>
      item.exportInfo?.dataset === 'mec-gl-data' ? 'MEC.GL' : item.exportInfo?.dataset || 'N/A',
    sortingField: 'exportInfo.dataset',
  },
  {
    id: 'requestTime',
    header: 'Request Time',
    cell: (item: ExportRecord) =>
      item.requestTime ? new Date(item.requestTime).toLocaleString() : 'N/A',
    sortingField: 'requestTime',
  },
  {
    id: 'expireTime',
    header: 'Expire Time',
    cell: (item: ExportRecord) =>
      item.expireTime ? new Date(item.expireTime).toLocaleString() : 'N/A',
    sortingField: 'expireTime',
  },
];

export const ExportsTable: React.FC<ExportsTableProps> = ({ controlId }) => {
  const [selectedItems, setSelectedItems] = useState<ExportRecord[]>([]);
  const [exports, setExports] = useState<ExportRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const { addFlashbarMessage } = useFlashbar();

  const loadExports = async () => {
    setIsLoading(true);
    try {
      const response = await getGLExports(controlId);
      console.log('Full response:', response);
      console.log('Records:', response.records);
      setExports((response.records || []) as ExportRecord[]);
    } catch (error) {
      console.error('Error loading exports:', error);
      addFlashbarMessage({
        type: 'error',
        header: 'Error',
        content: 'Failed to load exports. Please try again.',
        dismissible: true,
        id: 'exports_error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (controlId) {
      loadExports();
    }
  }, [controlId]);

  const { items, actions, filteredItemsCount, collectionProps, filterProps, paginationProps } =
    useCollection(exports, {
      filtering: {
        empty: (
          <Box textAlign="center" color="inherit">
            <b>No exports</b>
            <Box variant="p" color="inherit">
              No exports to display.
            </Box>
          </Box>
        ),
        noMatch: (
          <Box textAlign="center" color="inherit">
            <b>No matches</b>
            <Box variant="p" color="inherit">
              We can't find a match.
            </Box>
            <Button onClick={() => actions.setFiltering('')}>Clear filter</Button>
          </Box>
        ),
      },
      pagination: { pageSize: 10 },
      sorting: {
        defaultState: {
          sortingColumn: columnDefinitions[4], // requestTime
          isDescending: true,
        },
      },
      selection: {},
    });

  const handleDownload = () => {
    if (selectedItems.length > 0) {
      setShowDownloadModal(true);
    }
  };

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <>
      <Table
        {...collectionProps}
        selectionType="single"
        selectedItems={selectedItems}
        onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems)}
        loading={isLoading}
        loadingText="Loading exports..."
        header={
          <Header
            counter={`(${exports.length})`}
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button onClick={loadExports}>Refresh</Button>
                <Button
                  onClick={handleDownload}
                  variant="primary"
                  disabled={
                    selectedItems.length === 0 ||
                    selectedItems[0]?.status === 'PENDING' ||
                    !selectedItems[0]?.fileLinks?.length
                  }
                >
                  Download
                </Button>
              </SpaceBetween>
            }
          >
            GL Data Exports
          </Header>
        }
        columnDefinitions={columnDefinitions}
        items={items}
        pagination={<Pagination {...paginationProps} />}
        filter={
          <TextFilter
            {...filterProps}
            countText={`${filteredItemsCount} ${filteredItemsCount === 1 ? 'match' : 'matches'}`}
            filteringPlaceholder="Find exports"
          />
        }
      />

      {showDownloadModal && selectedItems[0] && (
        <DownloadModal
          exportRecord={selectedItems[0]}
          onClose={() => setShowDownloadModal(false)}
        />
      )}
    </>
  );
};
